/**
 * ConversationList Component
 *
 * Displays list of conversations with preview, unread counts, and timestamps.
 * Used in both guide and customer dashboards.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-476 - Core Messaging Infrastructure (13 SP)
 * Location: apps/web/src/components/messages/ConversationList.jsx
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import Avatar from '../common/Avatar';

/**
 * ConversationList Component
 *
 * @param {Object} props
 * @param {Array} props.conversations - Array of conversation objects
 * @param {string} props.selectedConversationId - Currently selected conversation ID
 * @param {Function} props.onSelectConversation - Callback when conversation is clicked
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
const ConversationList = ({
  conversations = [],
  selectedConversationId,
  onSelectConversation,
  loading = false,
  error = null,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <svg
          className="w-16 h-16 mb-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <p className="text-sm font-medium">No conversations yet</p>
        <p className="text-xs text-gray-400 mt-1">Messages will appear here</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {conversations.map((conversation) => (
        <ConversationListItem
          key={conversation.id}
          conversation={conversation}
          isSelected={conversation.id === selectedConversationId}
          onClick={() => onSelectConversation(conversation.id)}
        />
      ))}
    </div>
  );
};

/**
 * ConversationListItem Component
 *
 * Individual conversation in the list
 */
const ConversationListItem = ({ conversation, isSelected, onClick }) => {
  const {
    id: _id,
    participant_name,
    participant_user_type,
    last_message_preview,
    last_message_at,
    unread_count,
  } = conversation;

  // Format timestamp
  const timeAgo = last_message_at
    ? formatDistanceToNow(new Date(last_message_at), { addSuffix: true })
    : '';

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left transition-colors hover:bg-gray-50 ${
        isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : ''
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <Avatar
          name={participant_name}
          size="lg"
          fallbackBg={participant_user_type === 'guide' ? 'bg-emerald-600' : 'bg-blue-600'}
          className="flex-shrink-0"
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {participant_name || 'Unknown User'}
            </h3>
            {unread_count > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-bold text-white bg-rose-600 rounded-full">
                {unread_count}
              </span>
            )}
          </div>

          {/* Message preview */}
          <p className="text-sm text-gray-600 truncate mb-1">
            {last_message_preview || 'No messages yet'}
          </p>

          {/* Timestamp */}
          {timeAgo && (
            <p className="text-xs text-gray-400">{timeAgo}</p>
          )}
        </div>
      </div>
    </button>
  );
};

export default ConversationList;
